import { useState } from 'react'
import { Check, Copy, Globe, Send, type LucideIcon } from 'lucide-react'
import { ACADEMY_WEB_URL } from '@/lib/academyLinks'
import { toast } from 'sonner'
import { messagesNow, useMessages } from '@/i18n'
import { messages } from './messages'

// Тот же формат, что у «Поделиться» в TMA (`ShareCourseTgButton`): бот академии
// открывает курс по параметру `startapp`. Адрес бота у дев- и прод-стенда разный.
const BOT_URL = import.meta.env.VITE_ACADEMY_BOT_URL as string | undefined
// Веб-академия (`front_web`): страница курса — `/library/{slug}`.
const WEB_URL = ACADEMY_WEB_URL

/** Без кода курса (`slug`) ссылок нет: номер курса «по ссылке» посторонним отдаёт 404. */
export const courseLinks = (
  slug: string | null | undefined,
): { label: string; url: string; icon: LucideIcon }[] => {
  if (!slug) return []
  const m = messagesNow(messages)
  return [
    ...(BOT_URL
      ? [
          {
            label: m.inTelegram,
            url: `${BOT_URL}?startapp=shareCourse_${slug}_gamified`,
            icon: Send,
          },
        ]
      : []),
    { label: m.inWeb, url: `${WEB_URL}/library/${slug}`, icon: Globe },
  ]
}

/** В строке адрес без `https://` — он одинаков у всех ссылок и только съедал место. */
const shortUrl = (url: string): string => url.replace(/^https?:\/\//, '')

const COPIED_MS = 1500

const LinkRow = ({ label, url, icon: Icon }: { label: string; url: string; icon: LucideIcon }) => {
  const m = useMessages(messages)
  const [copied, setCopied] = useState(false)
  const copy = () => {
    void navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), COPIED_MS)
        toast.success(m.linkCopied)
      })
      .catch(() => toast.error(m.linkCopyFailed))
  }
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-foreground/[0.06] text-muted-foreground">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          {shortUrl(url)}
        </a>
      </div>
      <button
        type="button"
        onClick={copy}
        aria-label={m.copyLink(label)}
        className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.08] hover:text-foreground"
      >
        {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
      </button>
    </li>
  )
}

/** Ссылки на курс у учеников — видны, как только курс открыт. Одна карточка
 *  со списком: значок площадки, название, короткий адрес и копирование. */
export const CourseAppLink = ({ slug }: { slug: string | null | undefined }) => {
  const m = useMessages(messages)
  const links = courseLinks(slug)
  if (links.length === 0) return null
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{m.courseLink}</p>
      <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
        {links.map((l) => (
          <LinkRow key={l.label} label={l.label} url={l.url} icon={l.icon} />
        ))}
      </ul>
    </div>
  )
}
